// ** React Imports **
import { useState, useRef } from "react";
import { useSnackbar } from 'notistack';

// ** Components **
import StepProgressIndicator from '../../../components/authentication/StepProgressIndicator';

// ** Styles **
import './RegisterStepImagesUpload.css';

/**
 * RegisterStepImagesUpload Component (Step 3)
 * 
 * Business images upload step
 * 
 * Features:
 * - Upload business profile image
 * - Upload business cover image
 * - Preview uploaded images
 * - File validation
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.userData - User data from Step 0
 * @param {Object} props.businessData - Business data from Step 1
 * @param {Object} props.locationData - Location data from Step 2
 * @param {Object} props.initialData - Pre-filled data if returning to this step
 * @param {Function} props.onComplete - Callback when step completed
 */
const RegisterStepImagesUpload = ({ userData, businessData, locationData, initialData, onComplete, onStepClick }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // File inputs refs
  const profileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  // Image states
  const [profileImage, setProfileImage] = useState(initialData?.profileImage || null);
  const [profileImagePreview, setProfileImagePreview] = useState(initialData?.profileImagePreview || null);
  const [coverImage, setCoverImage] = useState(initialData?.coverImage || null);
  const [coverImagePreview, setCoverImagePreview] = useState(initialData?.coverImagePreview || null);

  /**
   * Validate image file
   */
  const validateImageFile = (file) => {
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      enqueueSnackbar("please upload jpeg or png image only", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
      return false;
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      enqueueSnackbar("image size must be less than 5mb", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
      return false;
    }

    return true;
  };

  /**
   * Handle profile image upload
   */
  const handleProfileImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && validateImageFile(file)) {
      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Handle cover image upload
   */
  const handleCoverImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && validateImageFile(file)) {
      setCoverImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setCoverImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Images are optional - can proceed without them
      // Move to next step with images data (or null if not uploaded)
      onComplete({
        profileImage: profileImage,
        profileImagePreview: profileImagePreview,
        coverImage: coverImage,
        coverImagePreview: coverImagePreview,
      });
    } catch (error) {
      console.error("Images upload error:", error);
      enqueueSnackbar("please try again", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-step-images">
      <div className="register-step-images__card">
        {/* Progress Indicator */}
        <StepProgressIndicator currentStep={3} totalSteps={3} onStepClick={onStepClick} />

        {/* Title */}
        <h2 className="register-step-images__title">business profile picture</h2>
        <p className="register-step-images__subtitle" style={{ 
          textAlign: 'center', 
          color: '#888', 
          fontSize: '14px', 
          marginTop: '-12px',
          marginBottom: '24px' 
        }}>
          optional - you can skip this step
        </p>

        {/* Form */}
        <form noValidate autoComplete="off" onSubmit={handleSubmit} className="register-step-images__form">
          
          {/* Business Profile Image */}
          <div className="register-step-images__field">
            <label className="register-step-images__label">business profile (optional)</label>
            
            <div className="register-step-images__upload-area">
              {profileImagePreview ? (
                <div className="register-step-images__preview">
                  <img 
                    src={profileImagePreview} 
                    alt="profile preview" 
                    className="register-step-images__preview-img"
                  />
                  <button
                    type="button"
                    className="register-step-images__change-btn"
                    onClick={() => profileInputRef.current?.click()}
                  >
                    change image
                  </button>
                </div>
              ) : (
                <div 
                  className="register-step-images__upload-placeholder"
                  onClick={() => profileInputRef.current?.click()}
                >
                  <div className="register-step-images__upload-icon">📷</div>
                  <div className="register-step-images__upload-text">
                    upload business profile picture
                  </div>
                  <div className="register-step-images__upload-hint">
                    jpeg and png formats, up to 5mb
                  </div>
                  <button
                    type="button"
                    className="register-step-images__browse-btn"
                  >
                    browse file
                  </button>
                </div>
              )}
              
              <input
                ref={profileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleProfileImageUpload}
                className="register-step-images__file-input"
              />
            </div>
          </div>

          {/* Business Cover Image */}
          <div className="register-step-images__field">
            <label className="register-step-images__label">business cover (optional)</label>
            
            <div className="register-step-images__upload-area">
              {coverImagePreview ? (
                <div className="register-step-images__preview">
                  <img 
                    src={coverImagePreview} 
                    alt="cover preview" 
                    className="register-step-images__preview-img register-step-images__preview-img--cover"
                  />
                  <button
                    type="button"
                    className="register-step-images__change-btn"
                    onClick={() => coverInputRef.current?.click()}
                  >
                    change image
                  </button>
                </div>
              ) : (
                <div 
                  className="register-step-images__upload-placeholder"
                  onClick={() => coverInputRef.current?.click()}
                >
                  <div className="register-step-images__upload-icon">🖼️</div>
                  <div className="register-step-images__upload-text">
                    upload business cover picture
                  </div>
                  <div className="register-step-images__upload-hint">
                    jpeg and png formats, up to 5mb
                  </div>
                  <button
                    type="button"
                    className="register-step-images__browse-btn"
                  >
                    browse file
                  </button>
                </div>
              )}
              
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleCoverImageUpload}
                className="register-step-images__file-input"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="register-step-images__submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'processing...' : (profileImage || coverImage ? 'continue' : 'skip & continue')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegisterStepImagesUpload;


